import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

/**
 * FAQ AAC — 7 catégories, ~30 questions.
 * Groupé par thématique, accordéon par question.
 */
const FAQ = [
  {
    cat: 'Étapes de la formation',
    color: 'red',
    items: [
      {
        q: "En quoi consiste concrètement la conduite accompagnée ?",
        a: "Cela commence comme un permis classique : l'élève passe le Code et fait 20 h de conduite en auto-école (13 h en boîte auto). Ce n'est qu'après cette phase initiale, et un feu vert de l'enseignant, que peut commencer la conduite aux côtés de l'accompagnateur. Deux grandes phases : 1) formation initiale (code + 20 h + AFFI) ; 2) conduite accompagnée (1er RDV de 2 h + ≥ 3 000 km sur ≥ 1 an + 2 autres RDV pédagogiques).",
      },
      {
        q: "Combien y a-t-il de rendez-vous pédagogiques ?",
        a: "Trois RDV au total, réunissant l'élève, l'enseignant et l'accompagnateur : (1) le RDV « préalable » à la fin de la formation initiale ; (2) le RDV intermédiaire après ~6 mois / ~1 000 km pour corriger d'éventuelles mauvaises habitudes ; (3) le RDV final (à ~3 000 km) pour valider que le candidat est prêt à passer l'examen pratique.",
      },
      {
        q: "Quelles sont les conditions pour commencer à conduire avec un accompagnateur ?",
        a: "L'élève doit avoir : obtenu le code de la route, validé les 20 h de conduite minimum (13 h en boîte auto), reçu l'AFFI (attestation de fin de formation initiale), obtenu l'accord de l'assurance, participé au 1er rendez-vous pédagogique.",
      },
      {
        q: "Quand peut-on passer le code ?",
        a: "Dès 15 ans, avant ou pendant la formation initiale. C'est la même épreuve que pour un permis classique : 40 questions, minimum 35 bonnes réponses. Le code est valable 5 ans à compter de sa date d'obtention.",
      },
      {
        q: "Faut-il avoir le code avant de commencer les heures de conduite ?",
        a: "Pas forcément. L'apprentissage du code et les premiers cours de conduite peuvent se faire en même temps. Il est toutefois préférable d'avoir eu son code avant, pour pouvoir décrypter correctement l'environnement routier et les priorités. Le dispositif étant accessible dès 15 ans, il peut être intéressant de commencer à préparer le code à 14 ans.",
      },
      {
        q: "Combien d'heures de conduite en auto-école ?",
        a: "20 h de conduite minimum avec un enseignant (ou 13 h pour un permis en boîte automatique). L'enseignant peut décider d'ajouter des heures s'il pense que l'élève n'est pas encore prêt à passer à la phase accompagnée.",
      },
      {
        q: "Combien de temps dure la phase de conduite accompagnée ?",
        a: "1 an minimum. Même si les 3 000 km sont atteints en moins de 12 mois, cette période minimale d'un an est obligatoire pour pouvoir passer l'épreuve pratique. Profitez-en pour continuer à vous entraîner et accumuler de l'expérience.",
      },
      {
        q: "Peut-on arrêter la conduite accompagnée en cours de route ?",
        a: "Oui, il est possible d'arrêter la conduite accompagnée en cours de route et de passer à l'apprentissage classique. Si vous avez 17 ans, que vous avez validé vos 20 heures obligatoires et que l'enseignant vous a donné le feu vert, vous pouvez vous présenter à l'examen du permis.",
      },
    ],
  },
  {
    cat: 'Avantages',
    color: 'emerald',
    items: [
      {
        q: "Quels sont les avantages de la conduite accompagnée ?",
        a: "Meilleure préparation grâce aux kilomètres parcourus et à la diversité des situations (nuit, autoroute, nationale, météo difficile, autre véhicule, horaires variés). Plus de chances de réussite à l'examen (≈ 75 % contre ≈ 58 % tous candidats). Assurance généralement moins chère grâce à l'expérience accumulée. Période probatoire réduite (2 ans au lieu de 3). Et pour les familles, le plaisir de partager ces moments.",
      },
      {
        q: "Combien de temps dure la période probatoire ?",
        a: "En AAC : 2 ans sans infraction (contre 3 ans en permis classique). Les 12 points sont atteints plus rapidement.",
      },
    ],
  },
  {
    cat: "Rôle de l'accompagnateur",
    color: 'amber',
    items: [
      {
        q: "Qui peut être accompagnateur ?",
        a: "Pas forcément un parent. Il faut être titulaire du permis B depuis au moins 5 ans SANS interruption, avoir l'accord écrit de son assureur, ne pas avoir eu de condamnation liée à la conduite, être mentionné au contrat signé avec l'auto-école et participer aux rendez-vous pédagogiques.",
      },
      {
        q: "Peut-on avoir plusieurs accompagnateurs ?",
        a: "Oui, à condition qu'ils soient tous déclarés à l'auto-école dans le contrat signé. La diversité de styles de conduite est même bénéfique.",
      },
      {
        q: "L'accompagnateur est-il seul face à la responsabilité ?",
        a: "Non. L'accompagnateur reste en lien avec l'auto-école tout au long de la phase de conduite accompagnée. Les rendez-vous pédagogiques sont là pour faire le point, poser des questions et obtenir des conseils pratiques. Rappel : ces RDV ne commencent qu'après les 20 heures minimum et l'accord de l'enseignant.",
      },
      {
        q: "Je crains de ne pas être un bon accompagnateur…",
        a: "Rassurez-vous : quand vous prenez le relais, votre enfant n'est plus un débutant. Il a le code, réalisé ≥ 20 h de conduite et l'enseignant l'a jugé prêt. Votre rôle n'est pas d'enseigner les bases, mais d'offrir du temps de pratique dans des situations variées et de l'encadrer. Les RDV pédagogiques sont là pour vous guider.",
      },
      {
        q: "Que faire si l'élève n'a pas envie de conduire ?",
        a: "La motivation de l'élève est cruciale : sans envie de sa part, la conduite accompagnée ne peut pas fonctionner. Mais l'implication de l'accompagnateur compte aussi beaucoup — se rendre disponible, varier les trajets.",
      },
      {
        q: "En cas d'infraction, qui est responsable ?",
        a: "L'apprenti n'a pas encore de permis et donc pas de points. En cas d'excès de vitesse ou de feu rouge, c'est l'accompagnateur qui sera sanctionné du nombre de points et l'amende est envoyée au propriétaire du véhicule. Toute contravention doit être notée dans le carnet de suivi (lu par l'examinateur). Responsabilité juridique : l'accompagnateur peut voir sa responsabilité engagée s'il est établi qu'il n'a pas exercé la vigilance requise.",
      },
      {
        q: "Faut-il installer des doubles commandes dans la voiture ?",
        a: "Non. En conduite accompagnée, le véhicule n'est pas équipé de doubles commandes comme en auto-école. Votre rôle n'est pas de remplacer l'enseignant, mais d'accompagner l'élève après la première phase d'apprentissage et de superviser sa conduite.",
      },
    ],
  },
  {
    cat: 'La question de l\'âge',
    color: 'sky',
    items: [
      {
        q: "À quel âge peut-on commencer la conduite accompagnée ?",
        a: "Dès 15 ans. C'est l'un des avantages : on peut passer le code dès 15 ans (au lieu de 16 en formation classique) et démarrer les heures de conduite en auto-école.",
      },
      {
        q: "À quel âge peut-on conduire seul ?",
        a: "Pour conduire seul, il faut avoir obtenu le permis. Et avoir 17 ans minimum (règle applicable en AAC comme en formation classique depuis le 1er janvier 2024).",
      },
      {
        q: "15 ans, n'est-ce pas trop jeune ?",
        a: "Au contraire, c'est un âge idéal pour apprendre : jeune encadré, apprentissage progressif étalé sur ~2 ans. À 14 ans, certains roulent déjà en cyclomoteur ou en voiturette. Bien sûr, la maturité entre en jeu — les parents restent les meilleurs juges.",
      },
      {
        q: "Pourquoi commencer avant le bac plutôt qu'après ?",
        a: "Parce que c'est souvent une période où les jeunes sont plus disponibles et moins pris par les examens ou grandes échéances (Bac, Parcoursup…). Cela leur permet de se concentrer sur un objectif concret : l'apprentissage de la conduite.",
      },
      {
        q: "Y a-t-il un risque de conflit familial pendant l'apprentissage ?",
        a: "Non. Les retours des familles sont globalement très positifs : relation souvent plus apaisée que redouté, moments de partage et de complicité. Beaucoup de jeunes trouvent rassurant de conduire aux côtés d'un proche. Si la situation devient tendue, n'hésitez pas à en parler avec l'auto-école (médiation, ajustements, voire arrêt possible).",
      },
    ],
  },
  {
    cat: 'Coût, assurances et financement',
    color: 'fuchsia',
    items: [
      {
        q: "La conduite accompagnée coûte-t-elle plus cher ?",
        a: "Oui mais… non : le forfait AAC apparaît plus élevé car il comprend, en plus des 20 h obligatoires (13 h en boîte auto), les 3 rendez-vous pédagogiques (26 h en boîte manuelle, 19 h en automatique). Mais grâce à leur meilleure préparation, les candidats AAC paient souvent moins au final : ils réussissent plus fréquemment du premier coup, évitant les frais supplémentaires d'heures et de nouvelle présentation à l'examen.",
      },
      {
        q: "Comment fonctionne l'assurance pendant la phase accompagnée ?",
        a: "Il faut souscrire une extension de garantie auprès de l'assurance du véhicule utilisé. Souvent gratuite ou d'un coût modique, elle couvre le jeune conducteur pendant toute la phase. L'accompagnateur peut faire la demande dès l'inscription de l'enfant à l'auto-école. L'assureur rédigera un avenant au contrat, ajoutant le nom du jeune conducteur accompagné.",
      },
      {
        q: "L'assurance sera-t-elle moins chère une fois le permis obtenu ?",
        a: "Oui. C'est l'un des avantages de l'AAC. Les assureurs considèrent que le jeune conducteur a déjà de l'expérience : tarifs plus avantageux dès l'obtention (surprime réduite ou même supprimée par certains assureurs), et période probatoire réduite (2 ans au lieu de 3) accélérant l'accès à des conditions plus favorables.",
      },
      {
        q: "Existe-t-il des aides pour financer la conduite accompagnée ?",
        a: "Oui. Les aides du permis classique sont généralement mobilisables. Notamment le « permis à 1 € par jour » : prêt à taux zéro pour les 15-25 ans, proposé par l'État via les banques partenaires. Certaines régions, départements ou employeurs proposent des aides complémentaires. Renseignez-vous auprès de votre auto-école ou du conseil régional.",
      },
    ],
  },
  {
    cat: 'Règles à respecter',
    color: 'red',
    items: [
      {
        q: "Peut-on conduire seul pendant la conduite accompagnée ?",
        a: "Non. Pendant toute la phase, vous devez conduire en présence de votre accompagnateur, assis à l'avant à côté de vous. Il doit être en état de veiller à la sécurité de tous et d'intervenir ou conduire si besoin.",
      },
      {
        q: "Peut-on conduire à l'étranger ?",
        a: "Non, la circulation à l'étranger est interdite. Vous devez circuler en France uniquement.",
      },
      {
        q: "Faut-il un signe distinctif sur le véhicule ?",
        a: "Oui. Vous devez mettre le macaron « conduite accompagnée » à l'arrière gauche du véhicule. Absence = amende de 11 € à 135 €.",
      },
      {
        q: "Peut-on conduire de nuit, sur autoroute, sous la pluie ?",
        a: "Bien sûr ! C'est même l'avantage : pratiquer dans des situations variées (nuit, autoroute, mauvaise météo…) qui ne sont pas toujours abordées dans l'apprentissage classique.",
      },
      {
        q: "Quelles sont les limitations de vitesse à respecter ?",
        a: "110 km/h sur autoroutes limitées à 130 · 100 km/h sur autres autoroutes · 100 km/h sur routes à 2 chaussées séparées par un terre-plein central · 80 km/h sur autres routes · 50 km/h (ou 30 km/h) en agglomération.",
      },
    ],
  },
  {
    cat: 'Quel véhicule ?',
    color: 'sky',
    items: [
      {
        q: "Peut-on conduire n'importe quelle voiture ?",
        a: "Oui, tout véhicule de catégorie B, à condition que la voiture remplisse les critères réglementaires (contrôle technique à jour, assurance adaptée…). Si la formation initiale a été faite en boîte manuelle, vous pouvez conduire une manuelle. Si elle a été faite en boîte automatique, vous devez conduire une automatique et le permis obtenu sera limité à l'automatique (levée possible via formation complémentaire de 7 h).",
      },
      {
        q: "Peut-on utiliser plusieurs véhicules ?",
        a: "Oui, mais il faut une assurance pour chaque véhicule.",
      },
      {
        q: "Peut-on changer de véhicule en cours d'apprentissage ?",
        a: "Oui, par exemple si la famille acquiert un nouveau véhicule. Il faut simplement s'assurer que le nouveau véhicule est bien couvert par l'assurance pour la conduite accompagnée.",
      },
    ],
  },
];

const COLOR_MAP = {
  red:     { border: 'border-red-500/40',     bg: 'bg-red-500/10',     text: 'text-red-300' },
  emerald: { border: 'border-emerald-500/40', bg: 'bg-emerald-500/10', text: 'text-emerald-300' },
  amber:   { border: 'border-amber-500/40',   bg: 'bg-amber-500/10',   text: 'text-amber-300' },
  sky:     { border: 'border-sky-500/40',     bg: 'bg-sky-500/10',     text: 'text-sky-300' },
  fuchsia: { border: 'border-fuchsia-500/40', bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-300' },
};

function FaqItem({ item, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 p-4 hover:bg-white/[0.03] text-left transition"
      >
        <div className="flex items-start gap-2.5 flex-1">
          <HelpCircle size={16} className="text-white/40 shrink-0 mt-0.5" />
          <div className="text-white text-[14px] font-semibold leading-snug">{item.q}</div>
        </div>
        <ChevronDown size={16} className={`text-white/40 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-11 text-white/70 text-[13.5px] leading-relaxed">{item.a}</div>
      )}
    </div>
  );
}

export default function AacFaq() {
  return (
    <div className="space-y-8" data-testid="aac-faq">
      {FAQ.map((group) => {
        const c = COLOR_MAP[group.color] || COLOR_MAP.sky;
        return (
          <div key={group.cat}>
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${c.border} ${c.bg} ${c.text} text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-3`}>
              {group.cat}
            </div>
            <div className="space-y-2">
              {group.items.map((it, i) => <FaqItem key={i} item={it} idx={i} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
